// src/pages/Register.jsx
import { useMemo, useState } from "react";
import api from "../services/api";
import { useNavigate, Link } from "react-router-dom";
import Modal from "../components/Modal";
import TermsContent from "./policies/TermsContent";
import PrivacyContent from "./policies/PrivacyContent";

const USERNAME_RE = /^[A-Za-z0-9_]{3,30}$/;

function validatePassword(pw) {
  return {
    length: pw.length >= 8 && pw.length <= 72,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
  };
}

// normalize common error payload shapes
function extractError(err) {
  const res = err?.response;
  if (!res) return "Network error. Please try again.";
  const data = res.data;

  // plain string from backend
  if (typeof data === "string") return data;

  // typical shapes
  if (data?.message) return data.message;
  if (data?.error) return data.error;

  // bean validation array
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors
      .map((e) => e.defaultMessage || e.message || String(e))
      .join("\n");
  }

  return `Request failed (${res.status || "?"}).`;
}

export default function Register() {
  const [formData, setFormData] = useState({
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
    birthDate: "",
    termsAccepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // field-level errors to highlight inputs
  const [fieldErrors, setFieldErrors] = useState({ email: "", username: "" });

  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const navigate = useNavigate();

  const maxDob = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    return d.toISOString().slice(0, 10);
  }, []);

  const pwRules = validatePassword(formData.password);
  const pwValid = Object.values(pwRules).every(Boolean);
  const usernameValid = USERNAME_RE.test(formData.username);
  const pwMatch =
    formData.confirmPassword.length > 0 &&
    formData.password === formData.confirmPassword;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // clear field error when editing that field
    if (name === "email" || name === "username") {
      setFieldErrors((p) => ({ ...p, [name]: "" }));
    }

    if (name === "username") {
      const clean = value.replace(/[^A-Za-z0-9_]/g, "");
      setFormData((p) => ({ ...p, username: clean }));
      return;
    }
    setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setFieldErrors({ email: "", username: "" });

    if (!usernameValid)
      return setMessage("Username must be 3–30 chars, letters/numbers/underscore only.");
    if (!pwValid)
      return setMessage("Password must be 8–72 chars and include upper, lower, number and special.");
    if (!pwMatch) return setMessage("Passwords do not match.");
    if (!formData.termsAccepted)
      return setMessage("You must accept the Terms and Privacy Policy to register.");

    setLoading(true);
    try {
      const payload = {
        email: formData.email,
        username: formData.username,
        password: formData.password,
        birthDate: formData.birthDate,
        termsAccepted: formData.termsAccepted,
      };
      await api.post("/api/auth/register", payload);

      // success -> go to verify screen
      navigate("/verify", { state: { email: formData.email } });
    } catch (err) {
      const msg = extractError(err);
      setMessage(msg);

      // try to set field-specific errors
      const status = err?.response?.status;
      const data = err?.response?.data;
      const lower = (msg || "").toLowerCase();

      // explicit field provided by backend (if you add it)
      if (data && typeof data === "object" && data.field) {
        setFieldErrors((p) => ({
          ...p,
          [data.field]: data.message || msg || "Invalid value.",
        }));
      } else {
        // infer from message/status
        const emailHit = lower.includes("email");
        const userHit = lower.includes("user");
        setFieldErrors((p) => ({
          ...p,
          email:
            (status === 409 || status === 400) && emailHit
              ? msg || "Email already in use."
              : p.email,
          username:
            (status === 409 || status === 400) && userHit
              ? msg || "Username already in use."
              : p.username,
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="mx-auto max-w-lg">
        <section className="card p-6 md:p-8">
          <header className="mb-6 text-center">
            <h1 className="font-display text-3xl md:text-4xl">
              Create your{" "}
              <span className="bg-gradient-to-r from-brand-500 to-glow bg-clip-text text-transparent">
                account
              </span>
            </h1>
            <p className="mt-2 text-slate-300">Join the community of DJs & house music fans.</p>
          </header>

          {message && (
            <div
              className="mb-4 rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-slate-200"
              role="alert"
              aria-live="polite"
            >
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="field">
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                aria-invalid={Boolean(fieldErrors.email)}
                className={`input ${fieldErrors.email ? "border-red-500/60" : ""}`}
                placeholder="you@example.com"
              />
              {fieldErrors.email && <p className="error">{fieldErrors.email}</p>}
            </div>

            <div className="field">
              <label htmlFor="username" className="label">
                Username
              </label>
              <input
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                autoComplete="username"
                maxLength={30}
                aria-invalid={Boolean(fieldErrors.username || (formData.username && !usernameValid))}
                className={`input ${
                  fieldErrors.username || (formData.username && !usernameValid)
                    ? "border-red-500/60"
                    : ""
                }`}
                placeholder="dj_marco"
              />
              {fieldErrors.username && <p className="error">{fieldErrors.username}</p>}
              {!fieldErrors.username && formData.username && !usernameValid && (
                <p className="error">3–30 chars. Letters, numbers, and underscore only.</p>
              )}
            </div>

            <div className="field">
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                maxLength={72}
                aria-invalid={Boolean(formData.password && !pwValid)}
                className={`input ${formData.password && !pwValid ? "border-red-500/60" : ""}`}
                placeholder="••••••••"
              />
              <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <Rule ok={pwRules.length} text="8–72 characters" />
                <Rule ok={pwRules.upper} text="Uppercase letter" />
                <Rule ok={pwRules.lower} text="Lowercase letter" />
                <Rule ok={pwRules.digit} text="Number" />
                <Rule ok={pwRules.special} text="Special character" />
              </ul>
            </div>

            <div className="field">
              <label htmlFor="confirmPassword" className="label">
                Confirm password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
                maxLength={72}
                aria-invalid={Boolean(formData.confirmPassword && !pwMatch)}
                className={`input ${formData.confirmPassword && !pwMatch ? "border-red-500/60" : ""}`}
                placeholder="••••••••"
              />
              {formData.confirmPassword && !pwMatch && (
                <p className="error">Passwords do not match.</p>
              )}
            </div>

            <div className="field">
              <label htmlFor="birthDate" className="label">
                Date of birth
              </label>
              <input
                id="birthDate"
                name="birthDate"
                type="date"
                value={formData.birthDate}
                onChange={handleChange}
                required
                max={maxDob}
                className="input"
              />
              <p className="help">You must be 18+ to join.</p>
            </div>

            <label className="mt-2 flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={handleChange}
                required
                className="rounded border-white/20 bg-white/10"
              />
              <span>
                I accept the{" "}
                <button type="button" className="underline" onClick={() => setShowTerms(true)}>
                  Terms
                </button>{" "}
                &{" "}
                <button type="button" className="underline" onClick={() => setShowPrivacy(true)}>
                  Privacy Policy
                </button>
                .
              </span>
            </label>

            <button type="submit" disabled={loading} className="btn-primary w-full mt-2">
              {loading ? "Registering…" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-400">
            Already have an account?{" "}
            <Link to="/login" className="text-slate-200 underline">
              Log in
            </Link>
          </p>
        </section>
      </div>

      {/* Modals */}
      <Modal open={showTerms} onClose={() => setShowTerms(false)} title="Terms of Service">
        <TermsContent />
      </Modal>
      <Modal open={showPrivacy} onClose={() => setShowPrivacy(false)} title="Privacy Policy">
        <PrivacyContent />
      </Modal>
    </div>
  );
}

function Rule({ ok, text }) {
  return <li className={ok ? "text-green-300" : "text-slate-400"}>{ok ? "✓" : "•"} {text}</li>;
}
